import { forceChangePasswordApi, updateAdminUserApi, useAdminUser } from "@/api/services/admin/admin-user";
import { useMe } from "@/api/services/admin/auth";
import { useEnum } from "@/api/services/global/enum";
import { Button } from "@/components/elements/Button";
import { DetailItem } from "@/components/elements/DetailItem";
import { Form } from "@/components/elements/Form";
import { FormField } from "@/components/elements/FormField";
import { Input } from "@/components/elements/Input";
import { LoadingPage } from "@/components/elements/Loading/Loading";
import { MenuNavigate, TMenuContent } from "@/components/elements/MenuNavigate/MenuNavigate";
import { Modal } from "@/components/elements/Modal/Modal";
import { Select } from "@/components/elements/Select";
import { StickyFooter } from "@/components/elements/StickyFooter/StickyFooter";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { EAdminUserAccountStatus, EAdminUserStatus } from "@/constants/enum";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { useDialog } from "@/hooks/useDialog";
import { updateAdminUserSchema } from "@/features/system-settings/schema/updateAdminUserSchema";
import { useToast } from "@/hooks/useToast";
import { AppRoutes } from "@/routes/config";
import { divider, text } from "@/theme/colors";
import { setErrorForm, showError } from "@/utils/error";
import { findLabelByValue } from "@/utils/object";
import { getDynamicRoute } from "@/utils/url";
import { yupResolver } from "@hookform/resolvers/yup";
import { Card, CardContent, CardHeader, Grid, Stack, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";

type TAdminUserEditForm = {
  fullName: string;
  email: string;
  role: string;
  status: string;
};

const AdminUserEditPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const toast = useToast();
  const queryClient = useQueryClient();
  const { data, isLoading } = useAdminUser();
  const { data: me } = useMe();
  const isCurrentUser = me?.data?.id?.toString() === id;
  const passwordResetDialog = useDialog();
  const { data: adminUserAccountStatuses } = useEnum("EAdminUserAccountStatus");

  const user = data?.data;
  const isPasswordResetDisabled = useMemo(() => {
    if (!user?.isPasswordRegistered) return true;
    if (user?.status === EAdminUserStatus.DISABLED) return true;
    if (user?.accountStatus === EAdminUserAccountStatus.INVITATION_SENT || user?.accountStatus === EAdminUserAccountStatus.PASSWORD_RESET)
      return true;
  }, [user]);

  const handleForceChangePassword = async () => {
    if (!id) return;
    const confirmed = await passwordResetDialog.show();
    if (!confirmed) return;
    try {
      await forceChangePasswordApi({ adminUserId: Number(id) });
      toast.success(API_SUCCESS_MESSAGE);
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ADMIN_USER.FETCH_DETAIL] });
    } catch (error) {
      showError(error);
    }
  };

  const methods = useForm<TAdminUserEditForm>({
    defaultValues: { fullName: "", email: "", role: "", status: "" },
    resolver: yupResolver(updateAdminUserSchema),
    mode: "all",
    reValidateMode: "onBlur",
  });
  const { control, reset } = methods;

  useEffect(() => {
    if (!data?.data) return;
    const { fullName, email, role, status } = data.data;
    reset({
      fullName: fullName ?? "",
      email: email ?? "",
      role: role ?? "",
      status: status ?? "",
    });
  }, [data, reset]);

  const onSubmit = async (formData: TAdminUserEditForm) => {
    if (!id) return;
    try {
      const { fullName, email, role, status } = formData;
      await updateAdminUserApi(id, { fullName, email, role, status });
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ADMIN_USER.FETCH_DETAIL, id] });
      toast.success(API_SUCCESS_MESSAGE);
      navigate(getDynamicRoute(AppRoutes.systemSettingsAdminUserDetail, { id }));
    } catch (error) {
      setErrorForm(methods, error);
      showError(error);
    }
  };

  const handleBack = () => {
    navigate(getDynamicRoute(AppRoutes.systemSettingsAdminUserDetail, { id }));
  };

  const contents: TMenuContent[] = useMemo(
    () => [
      {
        title: "ユーザーの状態",
        content: (
          <Card variant="outlined" sx={{ borderColor: divider.middle, borderRadius: "8px" }}>
            <CardHeader
              title={
                <Typography variant="head20Bold" color={text.primary}>
                  ユーザーの状態
                </Typography>
              }
              sx={{ pt: "24px", pb: 0, px: 3 }}
            />
            <CardContent sx={{ p: 3, "&:last-child": { pb: 3 } }}>
              <Grid container spacing={3}>
                <Grid item xs={user?.status === EAdminUserStatus.DISABLED || user?.accountStatus === EAdminUserAccountStatus.SUSPENDED ? 5.8 : 4.8}>
                  <FormField control={control} name="status">
                    <Select enumName="EAdminUserStatus" disabled={isCurrentUser} />
                  </FormField>
                </Grid>
                <Grid item xs={user?.status === EAdminUserStatus.DISABLED || user?.accountStatus === EAdminUserAccountStatus.SUSPENDED ? 5.8 : 4.8}>
                  <DetailItem
                    label="認証ステータス"
                    value={findLabelByValue(
                      adminUserAccountStatuses,
                      user?.status === EAdminUserStatus.DISABLED ? EAdminUserAccountStatus.SUSPENDED : user?.accountStatus,
                    )}
                  />
                </Grid>
                <Grid
                  item
                  xs={2.4}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Button size="sm" variant="ghost" disabled={isPasswordResetDisabled} onClick={handleForceChangePassword}>
                    パスワードリセット
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        ),
      },
      {
        title: "基本情報",
        content: (
          <Card variant="outlined" sx={{ borderColor: divider.middle, borderRadius: "8px" }}>
            <CardHeader
              title={
                <Typography variant="head20Bold" color={text.primary}>
                  基本情報
                </Typography>
              }
              sx={{ pt: "24px", pb: 0, px: 3 }}
            />
            <CardContent sx={{ p: 3, "&:last-child": { pb: 3 } }}>
              <Grid container spacing={3}>
                <Grid item xs={6}>
                  <FormField control={control} name="fullName">
                    <Input />
                  </FormField>
                </Grid>
                <Grid item xs={6}>
                  <FormField control={control} name="email">
                    <Input />
                  </FormField>
                </Grid>
                <Grid item xs={6}>
                  <FormField control={control} name="role">
                    <Select enumName="EAdminUserRole" />
                  </FormField>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        ),
      },
    ],
    [control, adminUserAccountStatuses, user, isPasswordResetDisabled, handleForceChangePassword, isCurrentUser],
  );

  if (isLoading) return <LoadingPage />;

  return (
    <Form methods={methods} onSubmit={onSubmit} schema={updateAdminUserSchema}>
      <Stack gap={3}>
        <Stack gap={1}>
          <Typography variant="head20Bold" color={text.primary}>
            管理ユーザーの編集
          </Typography>
          <Typography variant="body14" color={text.secondary}>
            いろはな管理ユーザーアカウントに関する情報を編集できます。
          </Typography>
        </Stack>

        <MenuNavigate
          contents={contents}
          contentAffix={
            <StickyFooter justifyContent="space-between">
              <Button size="sm" variant="ghost" onClick={handleBack}>
                戻る
              </Button>
              <Button size="sm" type="submit">
                保存
              </Button>
            </StickyFooter>
          }
        />
      </Stack>

      <Modal
        isOpen={passwordResetDialog.open}
        title="パスワードリセット"
        labelOk="リセットする"
        labelClose="キャンセル"
        onOk={passwordResetDialog.confirm}
        onClose={passwordResetDialog.cancel}
      >
        <Stack my={2}>
          <Typography variant="body14" color={text.secondary}>
            このユーザーのパスワードをリセットしますか？
          </Typography>
        </Stack>
      </Modal>
    </Form>
  );
};

export default AdminUserEditPage;
